import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useGlobalState } from '../context/GlobalStateContext';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import ProtectedRoute from './ProtectedRoute';
import {  Loader2  } from './icons/LucideIcons';
// Путь ученика — главная → вход → курс — грузится сразу, а не по требованию:
// пока догружался кусок следующей страницы, на секунду показывался белый экран.
import HomePage from './HomePage';
import ReadCourse from './ReadCourse';

// Пока грузится кусок страницы — спокойная точка по центру, а не спиннер под
// пустой шапкой: это третье по счёту «ожидание», которое видел посетитель.
const LoadingSpinner = React.memo(() => (
  <div className="flex justify-center items-center py-24" aria-label="Загрузка">
    <Loader2 className="h-6 w-6 animate-spin text-brand-400" />
  </div>
));

// Lazy loaded components
const Home = lazy(() => import('./Home'));
const ReadActivity = lazy(() => import('./ReadActivity'));

function RedirectToRead() {
  return <Navigate to="/read" replace />;
}
// Отдельная страница входа админа была копией входа в старом тёмном стиле,
// со своим паролем прямо в коде. Вход один — /login, он и админа узнаёт.
function RedirectToLogin() {
  return <Navigate to="/login" replace />;
}
const MonthlyReport = lazy(() => import('./MonthlyReport'));
const StudentManagement = lazy(() => import('./StudentManagement'));
const StudentPage = lazy(() => import('./StudentPage'));
const SVGInvoiceGenerator = lazy(() => import('./SVGInvoiceGenerator'));
const LessonTermsPage = lazy(() => import('./LessonTermsPage'));
const PriceListPage = lazy(() => import('./PriceListPage'));
const Progress = lazy(() => import('./Progress'));
const Library = lazy(() => import('./Library'));
const SetPassword = lazy(() => import('./SetPassword'));
const InstallPWA = lazy(() => import('./InstallPWA'));
const SurveyForm = lazy(() => import('./SurveyForm'));
const OnlineSession = lazy(() => import('./OnlineSession'));
const MusicPage = lazy(() => import('./MusicPage'));
const IrregularVerbs = lazy(() => import('./IrregularVerbs'));
const ReadingSheets = lazy(() => import('./ReadingSheets'));
const Account = lazy(() => import('./Account'));
const SheetsFull = lazy(() => import('./SheetsFull'));
const TermsOfService = lazy(() => import('./legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./legal/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./legal/RefundPolicy'));
const PublicOffer = lazy(() => import('./legal/PublicOffer'));

// Telegram Homework components
const HomeworkLibrary = lazy(() => import('./homework/HomeworkLibraryNew'));
const ModernHomeworkLibrary = lazy(() => import('./homework/ModernHomeworkLibrary'));
const HomeworkAssignment = lazy(() => import('./homework/HomeworkAssignment'));

// Constants
const ROUTES = {
  PUBLIC: [
    { path: '/', Component: HomePage },
    { path: '/SurveyForm', Component: SurveyForm },
    // Один вход для всех: Google, Telegram, логин и пароль (ученики, бот, админ).
    // Старая страница Login.js — только логин и сразу курс — больше не открывается.
    { path: '/login', Component: Account },
    { path: '/admin-login', Component: RedirectToLogin },
      // Кабинет ученика больше не входная точка: вход ведёт сразу на курс.
    // Старые ссылки и закладки не должны упираться в тупик, поэтому оба
    // адреса переводим на /read. Сам StudentDashboard.js никуда не делся:
    // чтобы вернуть кабинет, достаточно снова его импортировать.
    { path: '/dashboard', Component: RedirectToRead },
    { path: '/dashboard/:id', Component: RedirectToRead },
  { path: '/music', Component: MusicPage },
  // Тренажёр открыт всем: это вход на сайт из поиска и из Threads.
  { path: '/verbs', Component: IrregularVerbs },
  // Шпаргалки «Правила чтения» — подарок подписчикам; ссылку дают в группе, на сайте её нет.
  { path: '/sheets', Component: ReadingSheets },
  { path: '/account', Component: Account },
  { path: '/sheets/full', Component: SheetsFull },
  { path: '/terms-of-service', Component: TermsOfService },
  { path: '/privacy-policy', Component: PrivacyPolicy },
  { path: '/refund-policy', Component: RefundPolicy },
  { path: '/offer', Component: PublicOffer }
  ],  PROTECTED: [
    { path: '/read', Component: ReadCourse },
    { path: '/admin', Component: Home },
    { path: '/read-activity', Component: ReadActivity },
    { path: '/monthly-report', Component: MonthlyReport },
    { path: '/students', Component: StudentManagement },
    { path: '/students/:id/:tab?', Component: StudentPage },
    { path: '/invoice-generator', Component: SVGInvoiceGenerator },
    { path: '/usloviya', Component: LessonTermsPage },
    { path: '/prices', Component: PriceListPage },
    { path: '/progress', Component: Progress },
    { path: '/Library', Component: Library },
    { path: '/set-password', Component: SetPassword },
    { path: '/online-session', Component: OnlineSession },
    // Only keep the assignment route, library is now in the main Library component
    { path: '/admin/telegram-homework-assignment', Component: HomeworkAssignment }
  ]
};

function AppContent() {
  const [state, setState] = useState({
    isLoading: false,
    error: null
  });

  const { authenticatedStudent } = useGlobalState();
  const location = useLocation();
  // Meta Pixel: the snippet in index.html counts the first page only; in a
  // single-page app every route change is a new page view.
  const firstView = React.useRef(true);
  useEffect(() => {
    if (firstView.current) { firstView.current = false; return; }
    if (typeof window.fbq === 'function') window.fbq('track', 'PageView');
  }, [location.pathname]);
  const [searchParams] = useSearchParams();
  const isHome = location.pathname === '/';
  // У входа и у курса ученика своя шапка. Тёмная полоса сайта над ними была
  // второй шапкой и вторым стилем на одном экране; админу она нужна — там меню.
  const isStudentRead = location.pathname.startsWith('/read') && localStorage.getItem('isAdmin') !== 'true';
  // Тренажёр открыт всем и приходит по ссылке с лендинга. Тёмная полоса сайта
  // над ним выглядела как другой сайт, поэтому у него своя шапка.
  const bare = isHome || location.pathname === '/login' || isStudentRead
    || location.pathname === '/verbs' || location.pathname === '/sheets' || location.pathname === '/sheets/full' || location.pathname === '/account';

  // Batch state updates
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const handleUrlParameters = () => {
      const params = new URLSearchParams(location.search);
      
      try {
        // Clean up URL if needed
        if (location.search) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error) {
        console.error('URL parameter handling error:', error);
      }
    };

    handleUrlParameters();
  }, [location.search]);

  return (
    <>
      <Suspense fallback={<LoadingSpinner />}>
        <div className="flex flex-col min-h-screen">
          {/* Only show Navbar if not on HomePage */}
          {!bare && (
            /* This wrapper is positioned with a z-index, so it opens a
               stacking context and everything inside it — the Navbar's own
               z-50 and every dropdown panel — was capped at z-10. Page
               headers sit at z-20 (Урок) and z-30 (страница ученика), so they
               painted straight over the open menu. The value is inline
               because this build does not emit Tailwind arbitrary values. */
            <div className="sticky top-0 w-full" style={{ zIndex: 100 }}>
              <Navbar />
            </div>
          )}
          <main className="flex-1" style={bare ? { marginTop: 0 } : undefined}>
            <div className={bare ? 'app' : 'app main-content'}>
              <Routes>
                {/* Public routes */}
                {ROUTES.PUBLIC.map(({ path, Component }) => (
                  <Route key={path} path={path} element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Component />
                    </Suspense>
                  } />
                ))}

                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  {ROUTES.PROTECTED.map(({ path, Component }) => (
                    <Route key={path} path={path} element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <Component />
                      </Suspense>
                    } />
                  ))}
                </Route>

                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>

              {/* «Install App» only inside the reading course, where installing
                  makes sense; on the landing, /verbs and /sheets it was a stray
                  bare link that even came out in print (Роман, 29.10). */}
              {location.pathname.startsWith('/read') && (
                <Suspense fallback={null}>
                  <InstallPWA />
                </Suspense>
              )}
            </div>
          </main>
        </div>
      </Suspense>
      <Toaster />
    </>
  );
}

export default React.memo(AppContent);